import {
    Command,
    CommandRegistry,
    isOSX,
    MenuContribution,
    MenuModelRegistry,
    PreferenceContribution,
    PreferenceSchemaService
} from '@theia/core';
import {
    ApplicationShell,
    CommonCommands,
    CommonMenus,
    FrontendApplication,
    FrontendApplicationContribution,
    KeybindingRegistry
} from '@theia/core/lib/browser';
import { ContextMatcher } from '@theia/core/lib/browser/context-key-service';
import {
    BrowserMainMenuFactory,
    DynamicMenuBarWidget,
    DynamicMenuWidget,
    MenuBarWidget
} from '@theia/core/lib/browser/menu/browser-menu-plugin';
import { FrontendApplicationConfigProvider } from '@theia/core/lib/browser/frontend-application-config-provider';
import { WindowCommands } from '@theia/core/lib/browser/window-contribution';
import { ElectronMainMenuFactory } from '@theia/core/lib/electron-browser/menu/electron-main-menu-factory';
import { ElectronMenuOptions } from '@theia/core/lib/electron-browser/menu/electron-main-menu-factory';
import { MenuDto } from '@theia/core/lib/electron-common/electron-api';
import {
    MAIN_MENU_BAR,
    MenuPath,
    CompoundMenuNode,
    MenuNode,
    RenderedMenuNode
} from '@theia/core/lib/common/menu/menu-types';
import { PreferenceService } from '@theia/core/lib/common/preferences/preference-service';
import { CommandRegistry as LuminoCommandRegistry } from '@theia/core/shared/@lumino/commands';
import { inject, injectable, optional } from '@theia/core/shared/inversify';
import { WorkspaceCommands } from '@theia/workspace/lib/browser/workspace-commands';
import { FILE_WORKSPACE } from '@theia/workspace/lib/browser/workspace-frontend-contribution';
import { showTheiaTopPanel } from './application-shell';
import {
    isEditorChromeLocked,
    shouldIncludeMenuNodeWhenLocked,
    shouldShowTopLevelMenuWhenLocked
} from './editor-chrome-lock';
import { AUTH_LOGGED_IN_CONTEXT_KEY } from './auth-context-contribution';

// -----------------------------------------------------------------------------
// Top-level menu paths — shared with other contributions that register items.
// -----------------------------------------------------------------------------

/** Top-level Account menu, placed immediately before Help. */
export const AccountMenu: MenuPath = [...MAIN_MENU_BAR, '8_account'];

/** Help menu groups — each group is a native menu separator. */
export const HelpMenuGroups: { RESOURCES: MenuPath; ABOUT: MenuPath } = {
    RESOURCES: [...CommonMenus.HELP, '1_resources'] as MenuPath,
    ABOUT: [...CommonMenus.HELP, '2_about'] as MenuPath
} as const;

// -----------------------------------------------------------------------------
// Win/Linux in-window menubar — preference default and visibility after
// fullscreen / maximize. macOS uses the native menu bar (MacTitlebarContribution).
// -----------------------------------------------------------------------------

/** Body class when the Win/Linux in-window menu bar should stay on top of overlays. */
export const WIN_LINUX_MENUBAR_BODY_CLASS: string = 'lvgl-win-linux-menubar';

type ElectronTheiaCore = {
    onWindowEvent: (event: string, handler: () => void) => { dispose: () => void };
};

@injectable()
export class MenubarPreferenceContribution implements PreferenceContribution {
    async initSchema(service: PreferenceSchemaService): Promise<void> {
        service.updateSchemaProperty('window.menuBarVisibility', { default: 'visible' });
    }
}

@injectable()
export class MenubarContribution implements FrontendApplicationContribution {
    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(PreferenceService)
    protected readonly preferences!: PreferenceService;

    async onDidInitializeLayout(_app: FrontendApplication): Promise<void> {
        if (!this.isWinLinuxElectron()) return;

        document.body.classList.add(WIN_LINUX_MENUBAR_BODY_CLASS);
        this.ensureMenubarVisible();

        const core = (window as unknown as { electronTheiaCore?: ElectronTheiaCore }).electronTheiaCore;
        if (!core) return;

        const onWindowModeChange = (): void => {
            this.ensureMenubarVisible();
        };

        core.onWindowEvent('enter-full-screen', onWindowModeChange);
        core.onWindowEvent('leave-full-screen', onWindowModeChange);
        core.onWindowEvent('maximize', onWindowModeChange);
        core.onWindowEvent('unmaximize', onWindowModeChange);

        this.preferences.onPreferenceChanged((event) => {
            if (event.preferenceName === 'window.menuBarVisibility') {
                this.ensureMenubarVisible();
            }
        });
    }

    protected ensureMenubarVisible(): void {
        const visibility: string = this.preferences.get('window.menuBarVisibility') ?? 'visible';
        if (visibility === 'hidden' || visibility === 'compact') return;

        showTheiaTopPanel(this.shell);

        const menuBar: HTMLElement | null = this.shell.topPanel.node.querySelector<HTMLElement>('.lm-MenuBar');
        if (menuBar) {
            menuBar.style.visibility = 'visible';
            menuBar.style.pointerEvents = 'auto';
        }
    }

    protected isWinLinuxElectron(): boolean {
        return 'electronTheiaCore' in window && !/Mac/i.test(navigator.userAgent);
    }
}

// -----------------------------------------------------------------------------
// Menubar filtering while editor chrome is locked (auth, license, launcher, toolchain).
// -----------------------------------------------------------------------------

@injectable()
export class LvglBrowserMainMenuFactory extends BrowserMainMenuFactory {
    refreshMenuBar(menuBar: DynamicMenuBarWidget): void {
        this.showMenuBar(menuBar);
    }

    protected override fillMenuBar(menuBar: MenuBarWidget): void {
        const menuModel: MenuNode | undefined = this.menuProvider.getMenuNode(MAIN_MENU_BAR);
        if (!menuModel || !CompoundMenuNode.is(menuModel)) return;

        const menuCommandRegistry: LuminoCommandRegistry = new LuminoCommandRegistry();
        const locked: boolean = isEditorChromeLocked();
        for (const menu of menuModel.children) {
            if (CompoundMenuNode.is(menu) && RenderedMenuNode.is(menu)) {
                if (!menu.isVisible(MAIN_MENU_BAR, this.contextKeyService, undefined)) continue;
                if (locked && !shouldShowTopLevelMenuWhenLocked(menu.id)) continue;

                const menuWidget: DynamicMenuWidget = this.createMenuWidget(
                    MAIN_MENU_BAR,
                    menu,
                    this.contextKeyService,
                    {
                        commands: menuCommandRegistry
                    }
                );
                menuBar.addMenu(menuWidget);
            }
        }
    }
}

// -----------------------------------------------------------------------------
// macOS app menu — route About to the same command as Help → About.
// -----------------------------------------------------------------------------

@injectable()
export class LvglElectronMainMenuFactory extends ElectronMainMenuFactory {
    @inject(CommandRegistry)
    protected readonly commands!: CommandRegistry;

    refreshMenuBar(menuBar: DynamicMenuBarWidget): void {
        this.showMenuBar(menuBar);
    }

    protected override fillMenuBar(menuBar: MenuBarWidget): void {
        const menuModel: MenuNode | undefined = this.menuProvider.getMenuNode(MAIN_MENU_BAR);
        if (!menuModel || !CompoundMenuNode.is(menuModel)) return;

        const menuCommandRegistry: LuminoCommandRegistry = new LuminoCommandRegistry();
        const locked: boolean = isEditorChromeLocked();
        for (const menu of menuModel.children) {
            if (CompoundMenuNode.is(menu) && RenderedMenuNode.is(menu)) {
                if (!menu.isVisible(MAIN_MENU_BAR, this.contextKeyService, undefined)) continue;
                if (locked && !shouldShowTopLevelMenuWhenLocked(menu.id)) continue;

                const menuWidget: DynamicMenuWidget = this.createMenuWidget(
                    MAIN_MENU_BAR,
                    menu,
                    this.contextKeyService,
                    {
                        commands: menuCommandRegistry
                    }
                );
                menuBar.addMenu(menuWidget);
            }
        }
    }

    protected override fillMenuTemplate(
        parentItems: MenuDto[],
        menuPath: MenuPath,
        menu: MenuNode,
        args: unknown[] | undefined,
        contextMatcher: ContextMatcher,
        options: ElectronMenuOptions,
        skipRoot: boolean
    ): MenuDto[] {
        const effectivePath: MenuPath = (menu as { effectiveMenuPath?: MenuPath }).effectiveMenuPath || menuPath;
        if (!shouldIncludeMenuNodeWhenLocked(menu, effectivePath)) return parentItems;
        return super.fillMenuTemplate(parentItems, menuPath, menu, args, contextMatcher, options, skipRoot);
    }

    protected override createOSXMenu(): MenuDto {
        const appName: string = FrontendApplicationConfigProvider.get().applicationName;
        const menu: MenuDto = super.createOSXMenu();

        if (!menu.submenu?.length) return menu;

        const submenu: MenuDto[] = [...menu.submenu];
        const aboutItem: MenuDto = submenu[0];
        if (aboutItem && 'role' in aboutItem && aboutItem.role === 'about') {
            submenu[0] = {
                label: aboutItem.label ?? `About ${appName}`,
                execute: () => {
                    void this.commands.executeCommand(CommonCommands.ABOUT_COMMAND.id);
                }
            };
        }

        return { ...menu, submenu };
    }
}

// -----------------------------------------------------------------------------
// Menu item customization — File/Help/Account layout. Runs after core menus.
// -----------------------------------------------------------------------------

const PROJECT_LAUNCHER_COMMAND_ID: string = 'lvgl:project-launcher';

const PROJECTS_CATEGORY: string = 'Projects';

/** Command IDs outside WorkspaceCommands that still use "workspace" in their label. */
const GO_TO_SYMBOL_IN_PROJECT_COMMAND_ID: string = 'languages.workspace.symbol';
const TERMINAL_IN_ACTIVE_PROJECT_COMMAND_ID: string = 'terminal:new:active:workspace';

const DISABLED_COMMAND_HANDLER: { execute: () => undefined; isEnabled: () => false; isVisible: () => false } = {
    execute: () => undefined,
    isEnabled: () => false,
    isVisible: () => false
};

@injectable()
export class AppMenuContribution implements MenuContribution, FrontendApplicationContribution {
    @inject(CommandRegistry)
    protected readonly commands!: CommandRegistry;

    @inject(MenuModelRegistry)
    protected readonly menus!: MenuModelRegistry;

    @inject(KeybindingRegistry)
    protected readonly keybindings!: KeybindingRegistry;

    @inject(ElectronMainMenuFactory)
    @optional()
    protected readonly menuFactory?: ElectronMainMenuFactory;

    registerMenus(menus: MenuModelRegistry): void {
        menus.registerSubmenu(AccountMenu, 'Account', {
            sortString: '8_account',
            when: AUTH_LOGGED_IN_CONTEXT_KEY
        });
    }

    onStart(): void {
        this.customizeFileMenu();
        this.customizeProjectTerminology();
        this.customizeHelpMenu();
        this.menuFactory?.setMenuBar();
    }

    protected customizeFileMenu(): void {
        this.disableCommand(CommonCommands.NEW_UNTITLED_TEXT_FILE);
        this.disableCommand(WindowCommands.NEW_WINDOW);
        this.disableCommand(WorkspaceCommands.OPEN_WORKSPACE);
        this.disableCommand(WorkspaceCommands.OPEN_FILE);
        this.disableCommand(WorkspaceCommands.ADD_FOLDER);
        this.disableCommand(WorkspaceCommands.SAVE_WORKSPACE_AS);
        this.redirectCommandToProjectLauncher(WorkspaceCommands.OPEN, 'Open Project Launcher...');

        this.menus.unregisterMenuAction(WorkspaceCommands.OPEN.id, CommonMenus.FILE_OPEN);
        this.menus.unregisterMenuAction(WorkspaceCommands.OPEN_FILE.id, CommonMenus.FILE_OPEN);
        this.menus.unregisterMenuAction(WorkspaceCommands.OPEN_FOLDER.id, CommonMenus.FILE_OPEN);

        this.menus.unregisterMenuAction(WorkspaceCommands.OPEN_RECENT_WORKSPACE.id, CommonMenus.FILE_OPEN);

        this.menus.registerMenuAction(CommonMenus.FILE_OPEN, {
            commandId: WorkspaceCommands.OPEN.id,
            label: 'Open Project Launcher...',
            order: 'a00'
        });

        this.menus.registerMenuAction(CommonMenus.FILE_OPEN, {
            commandId: WorkspaceCommands.OPEN_RECENT_WORKSPACE.id,
            label: 'Open Recent Project...',
            order: 'a20'
        });
    }

    protected customizeProjectTerminology(): void {
        this.relabelCommand(WorkspaceCommands.OPEN_RECENT_WORKSPACE, 'Open Recent Project...');
        this.relabelCommand(WorkspaceCommands.CLOSE, 'Close Project', PROJECTS_CATEGORY);
        this.relabelCommand({ id: GO_TO_SYMBOL_IN_PROJECT_COMMAND_ID }, 'Go to Symbol in Project...');
        this.relabelCommand({ id: TERMINAL_IN_ACTIVE_PROJECT_COMMAND_ID }, 'Create New Terminal (In Active Project)');

        this.disableCommand(WorkspaceCommands.MANAGE_WORKSPACE_TRUST);
        this.disableCommand(WorkspaceCommands.REMOVE_FOLDER);
        this.disableCommand(WorkspaceCommands.OPEN_WORKSPACE_FILE);
    }

    protected customizeHelpMenu(): void {
        this.menus.unregisterMenuAction(CommonCommands.ABOUT_COMMAND, CommonMenus.HELP);

        if (isOSX) {
            return;
        }

        this.menus.registerMenuAction(HelpMenuGroups.ABOUT, {
            commandId: CommonCommands.ABOUT_COMMAND.id,
            label: CommonCommands.ABOUT_COMMAND.label,
            order: '0'
        });
    }

    protected disableCommand(command: Command): void {
        this.menus.unregisterMenuAction(command.id);
        this.menus.unregisterMenuAction(command.id, CommonMenus.FILE_NEW_TEXT);
        this.menus.unregisterMenuAction(command.id, CommonMenus.FILE_OPEN);
        this.menus.unregisterMenuAction(command.id, FILE_WORKSPACE);

        this.keybindings.unregisterKeybinding(command);

        this.commands.unregisterCommand(command.id);
        this.commands.registerCommand({ id: command.id }, DISABLED_COMMAND_HANDLER);
    }

    protected relabelCommand(command: Command, label: string, category?: string): void {
        const handlers = this.commands.getAllHandlers(command.id);
        const existing = this.commands.getCommand(command.id);

        this.commands.unregisterCommand(command.id);
        this.commands.registerCommand({
            id: command.id,
            label,
            category: category ?? existing?.category
        });

        for (const handler of handlers) {
            this.commands.registerHandler(command.id, handler);
        }
    }

    protected redirectCommandToProjectLauncher(command: Command, label: string): void {
        this.keybindings.unregisterKeybinding(command);

        this.commands.unregisterCommand(command.id);
        this.commands.registerCommand(
            {
                id: command.id,
                label,
                category: command.category
            },
            {
                execute: async (): Promise<void> => {
                    await this.commands.executeCommand(PROJECT_LAUNCHER_COMMAND_ID);
                },
                isEnabled: (): boolean => true,
                isVisible: (): boolean => true
            }
        );
    }
}
